import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  MessageSquare, Send, Sparkles, Loader2, Mail, User as UserIcon, Edit3, Eye,
} from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

export default function AdminMessages() {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [rawMessage, setRawMessage] = useState('');
  const [correctedMessage, setCorrectedMessage] = useState('');
  const [editableCorrected, setEditableCorrected] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);

  // Load clients on mount
  useEffect(() => {
    (async () => {
      try {
        setLoadingClients(true);
        const r = await api.get('/admin/clients');
        setClients(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Impossible de charger les clients');
      } finally {
        setLoadingClients(false);
      }
    })();
  }, []);

  const filteredClients = useMemo(() => {
    if (!search) return clients;
    const s = search.toLowerCase();
    return clients.filter((c) =>
      `${c.prenom || ''} ${c.nom || ''} ${c.email || ''}`.toLowerCase().includes(s),
    );
  }, [clients, search]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedId) || null,
    [clients, selectedId],
  );

  const handleCorrectAndPreview = async () => {
    if (!selectedId) {
      toast.error('Sélectionnez un client destinataire');
      return;
    }
    if (!subject.trim()) {
      toast.error('Renseignez un sujet');
      return;
    }
    if (!rawMessage.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }
    try {
      setGenerating(true);
      const r = await api.post(`/admin/clients/${selectedId}/send-message`, {
        subject: subject.trim(),
        message: rawMessage,
      });
      setCorrectedMessage(r.data?.corrected_message || '');
      setEditableCorrected(r.data?.corrected_message || '');
      setEditing(false);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la reformulation');
    } finally {
      setGenerating(false);
    }
  };

  const handleSend = async () => {
    if (!selectedId) return;
    const finalMessage = (editing ? editableCorrected : correctedMessage).trim();
    if (!finalMessage) {
      toast.error('Le message corrigé est vide');
      return;
    }
    try {
      setSending(true);
      await api.post(`/admin/clients/${selectedId}/send-message/confirm`, {
        subject: subject.trim(),
        message: finalMessage,
      });
      toast.success('Message envoyé au client', {
        description: selectedClient?.email,
      });
      // Reset form
      setPreviewOpen(false);
      setSubject('');
      setRawMessage('');
      setCorrectedMessage('');
      setEditableCorrected('');
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const renderHtmlPreview = (text) => {
    const paragraphs = text
      .replace(/\r\n/g, '\n')
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean);
    return paragraphs.length
      ? paragraphs.map((p, i) => (
          <p key={i} className="mb-3 leading-relaxed text-slate-800 dark:text-slate-200 whitespace-pre-line">
            {p}
          </p>
        ))
      : (
        <p className="text-slate-500 dark:text-slate-400 italic">(Message vide)</p>
      );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-blue-700 dark:text-blue-400" />
            {t('admin.messagesTitle')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('admin.messagesSubtitle')}
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Composer */}
        <Card className="lg:col-span-2 dark:bg-slate-900 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="dark:text-white flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Composer un message
            </CardTitle>
            <CardDescription className="dark:text-slate-400">
              Étape 1 : Saisissez votre brouillon. Étape 2 : Notre IA le reformule dans un ton bancaire professionnel.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Client picker */}
            <div className="space-y-2">
              <Label htmlFor="client-search" className="dark:text-slate-200">Client destinataire</Label>
              <Input
                id="client-search"
                placeholder="Rechercher par nom ou email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={loadingClients}
                data-testid="input-messages-search-client"
              />
              <Select value={selectedId} onValueChange={setSelectedId} disabled={loadingClients}>
                <SelectTrigger data-testid="select-messages-client" className="w-full">
                  <SelectValue placeholder={loadingClients ? 'Chargement…' : 'Sélectionnez un client'} />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {filteredClients.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-slate-500">Aucun client trouvé</div>
                  ) : (
                    filteredClients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.prenom} {c.nom} — {c.email}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedClient && (
                <div className="text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2 mt-1">
                  <UserIcon className="w-3.5 h-3.5" />
                  {selectedClient.prenom} {selectedClient.nom}
                  <span className="text-slate-400">·</span>
                  <Mail className="w-3.5 h-3.5" />
                  {selectedClient.email}
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject" className="dark:text-slate-200">Sujet du message</Label>
              <Input
                id="subject"
                placeholder="Ex. : Mise à jour de votre dossier"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="input-messages-subject"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message" className="dark:text-slate-200">Brouillon du message</Label>
              <Textarea
                id="message"
                rows={8}
                placeholder="Écrivez librement votre message. Il sera reformulé en style bancaire."
                value={rawMessage}
                onChange={(e) => setRawMessage(e.target.value)}
                data-testid="textarea-messages-raw"
                className="resize-y"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {rawMessage.length} caractère{rawMessage.length > 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={handleCorrectAndPreview}
                disabled={generating || !selectedId || !subject.trim() || !rawMessage.trim()}
                className="bg-blue-700 hover:bg-blue-800"
                data-testid="btn-messages-correct"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reformulation…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Corriger et prévisualiser</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Help card */}
        <Card className="dark:bg-slate-900 dark:border-slate-800">
          <CardHeader>
            <CardTitle className="dark:text-white text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Comment ça marche ?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold shrink-0">1</div>
              <p>Sélectionnez le client destinataire dans la liste ci-contre.</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold shrink-0">2</div>
              <p>Saisissez un sujet et un message libre, dans votre style habituel.</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold shrink-0">3</div>
              <p>Cliquez sur <em>Corriger et prévisualiser</em> : l'IA réécrit le texte en ton banque BFI.</p>
            </div>
            <div className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 flex items-center justify-center text-xs font-bold shrink-0">4</div>
              <p>Vérifiez l'aperçu de l'email, modifiez si besoin, puis <em>Envoyer</em>.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-700" />
              Aperçu de l'email
            </DialogTitle>
            <DialogDescription>
              Voici exactement ce que le client va recevoir. Vous pouvez encore modifier le texte avant envoi.
            </DialogDescription>
          </DialogHeader>

          {/* Recipient info */}
          {selectedClient && (
            <div className="bg-slate-50 dark:bg-slate-800/60 rounded-lg p-3 text-sm flex flex-wrap gap-x-6 gap-y-1 border dark:border-slate-700">
              <div><span className="text-slate-500 dark:text-slate-400">À :</span> <strong className="dark:text-white">{selectedClient.prenom} {selectedClient.nom}</strong></div>
              <div><span className="text-slate-500 dark:text-slate-400">Email :</span> <strong className="dark:text-white">{selectedClient.email}</strong></div>
              <div className="w-full"><span className="text-slate-500 dark:text-slate-400">Objet :</span> <strong className="dark:text-white">{subject}</strong></div>
            </div>
          )}

          {/* Email mock (mimics the SMTP template) */}
          <div className="rounded-xl overflow-hidden border shadow-sm bg-white dark:bg-slate-50">
            <div
              className="px-6 py-5"
              style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e3a8a 100%)' }}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-lg bg-white text-blue-900 font-extrabold flex items-center justify-center text-lg">
                  B
                </div>
                <span className="text-white text-xl font-semibold">BFI Groupe</span>
              </div>
            </div>
            <div className="px-7 py-8 text-slate-900">
              <p className="mb-3">
                Bonjour <strong>{selectedClient?.prenom || 'Client'}</strong>,
              </p>
              {editing ? (
                <Textarea
                  rows={10}
                  value={editableCorrected}
                  onChange={(e) => setEditableCorrected(e.target.value)}
                  data-testid="textarea-messages-edit-preview"
                  className="my-2"
                />
              ) : (
                <div>{renderHtmlPreview(correctedMessage)}</div>
              )}
              <p className="mt-6">
                Cordialement,<br />
                <strong>L'équipe BFI Groupe</strong>
              </p>
            </div>
            <div className="bg-slate-50 px-7 py-3 text-xs text-slate-500 text-center">
              BFI Groupe — Banque en ligne · contact@bfigroupefr.com
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={sending}>
              Annuler
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (editing) {
                  // commit edit back to corrected
                  setCorrectedMessage(editableCorrected);
                  setEditing(false);
                } else {
                  setEditableCorrected(correctedMessage);
                  setEditing(true);
                }
              }}
              disabled={sending}
              data-testid="btn-messages-toggle-edit"
            >
              <Edit3 className="w-4 h-4 mr-1" />
              {editing ? 'Aperçu' : 'Modifier'}
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="btn-messages-send"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Envoyer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
